import { KeyboardArrowDown } from '@mui/icons-material';
import { Autocomplete, TextField, AutocompleteProps } from '@mui/material';
import React from 'react';
import { Control, Controller } from 'react-hook-form';

interface FormAutocompleteProps<
    T extends { value: any; label: string },
> extends Omit<AutocompleteProps<T, false, boolean, false>, 'renderInput'> {
    name: string;
    options: T[];
    control: Control<any>;
    label?: string;
    customError?: string;
    placeholder?: string;
    shrinkLabel?: boolean;
    getOptionDisabled?: (option: T) => boolean;
    filterOptions?: (options: T[], state: object) => T[];
    startAdornment?: React.JSX.Element;
    disabled?: boolean;
    onOptionSelect?: (selectedOption: T | null) => void;
    fontSize?: string;
}

/**
 * For use within RHF FormProvider
 */
const FormAutocomplete = <T extends { value: any; label: string }>({
    name,
    control,
    options,
    label,
    placeholder,
    customError,
    startAdornment,
    shrinkLabel,
    onOptionSelect,
    fontSize,
    ...props
}: FormAutocompleteProps<T>) => {
    return (
        <Controller
            control={control}
            name={name}
            render={({
                field: { onChange, value, ref },
                fieldState: { error },
            }) => (
                <Autocomplete
                    size='small'
                    options={options}
                    disableClearable
                    value={
                        options.find((option) => option.value === value) ?? null
                    }
                    onChange={(_, data) => {
                        onChange(data?.value);
                        if (onOptionSelect) {
                            onOptionSelect(data || null);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={ref}
                            label={label}
                            error={!!error || !!customError}
                            helperText={(error && error.message) || customError}
                            placeholder={placeholder}
                            InputLabelProps={{ shrink: shrinkLabel }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment,
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize,
                                },
                            }}
                        />
                    )}
                    popupIcon={<KeyboardArrowDown />}
                    {...props}
                />
            )}
        />
    );
};

export default FormAutocomplete;
